import { useCallback, useState } from 'react';
import { ContentNode } from '@amzn/awscat-pattern-library-service-model';

import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { log } from '../../loggers/Logger';
import { SaveWorkingCopyError } from '../../services/Errors';
import { addSectionToPage } from '../../services/PageOperations';

/** Presentation state for the "Add section" button on the Contents tab. */
export interface AddSectionButtonViewModel {
  /** True while the new section is being saved to the working copy. */
  readonly isAdding: boolean;
  /** Inline error to display after a failed add attempt. Undefined when no error. */
  readonly addErrorMessage: string | undefined;
  /** Append a new "Untitled" section to the current page and persist. */
  readonly addSection: () => void;
}

export function useAddSectionButtonViewModel(
  currentPage: ContentNode,
  onSectionAdded?: (newSectionId: string) => void,
): AddSectionButtonViewModel {
  const service = useContentEditorService();

  const [isAdding, setIsAdding] = useState(false);
  const [addErrorMessage, setAddErrorMessage] = useState<string | undefined>(undefined);

  const addSection = useCallback(() => {
    if (isAdding) return;
    // Snapshot pre-add content so we can revert the stage if flush fails;
    // otherwise a retry would compound new sections on top of the failed one.
    const previousContent = service.content;
    if (!previousContent) return;
    const result = addSectionToPage(previousContent, currentPage.id);
    if (!result) return;

    setIsAdding(true);
    setAddErrorMessage(undefined);
    service.stageEdit(result.content);
    // onSectionAdded is fire-and-forget — we don't try to "undo" it on flush
    // failure. It's only used to highlight/autofocus the new section, so if
    // the staged edit gets reverted below, the caller's autofocus target just
    // vanishes from the tree. No state corruption, no retry hazard.
    onSectionAdded?.(result.section.id);

    void (async () => {
      try {
        await service.flush();
      } catch (error) {
        // Always revert the optimistic stage on failure so a retry doesn't
        // compound sections, regardless of which error type bubbled.
        service.stageEdit(previousContent);
        // Only handle the save-failure case inline; rethrowing anything else
        // turns it into an unhandled rejection so RUM's global
        // `unhandledrejection` listener reports it.
        if (!(error instanceof SaveWorkingCopyError)) throw error;
        log.error({ err: error }, 'Failed to save new section to working copy');
        setAddErrorMessage(
          'There was an error adding the section. Please wait a few seconds and retry.',
        );
      } finally {
        setIsAdding(false);
      }
    })();
  }, [isAdding, service, currentPage, onSectionAdded]);

  return {
    isAdding,
    addErrorMessage,
    addSection,
  };
}
